import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  CheckCircle as SuccessIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import { ausbildungOptions } from "../../../constants/ausbildungOptions";
import api from "lib/api/api";

const languageLevels = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Audio instructions are translated in locale files.

const AusbildungSubmissionForm = ({
  activeStep,
  formData,
  handleChange,
  handleFileUpload,
  handleMultipleFileUpload,
  handleDeleteFile,
  handleSubmit,
  isArabic,
  t,
  isEdit = false,
  lockDesiredProgram = false,
  validationErrors = {},
  isSubmitting = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordingError, setRecordingError] = useState("");
  const [positionRequirements, setPositionRequirements] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  useEffect(() => {
    if (!isRecording) return undefined;
    const timer = window.setInterval(() => {
      setRecordingSeconds((seconds) => {
        if (seconds >= 239) {
          stopRecording();
          return 240;
        }
        return seconds + 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isRecording]);

  useEffect(() => () => streamRef.current?.getTracks().forEach((track) => track.stop()), []);

  useEffect(() => {
    api.get("/ausbildung/categories")
      .then((response) => {
        const categories = Array.isArray(response.data) ? response.data : [];
        setPositionRequirements(categories.find((category) => category.name === formData.desiredProgram) || null);
      })
      .catch(() => setPositionRequirements(null));
  }, [formData.desiredProgram]);

  const startRecording = async () => {
    setRecordingError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      streamRef.current = stream;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => event.data.size && chunksRef.current.push(event.data);
      recorder.onstop = () => {
        const type = recorder.mimeType || "audio/webm";
        const extension = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const file = new File([new Blob(chunksRef.current, { type })], `ausbildung-audio-${Date.now()}.${extension}`, { type });
        handleFileUpload("audioMessage")({ target: { files: [file], value: "" } });
        stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
      };
      recorderRef.current = recorder;
      setRecordingSeconds(0);
      recorder.start();
      setIsRecording(true);
    } catch {
      setRecordingError(t("studentDashboard.submissions.ausbildungForm.microphoneError"));
    }
  };

  const renderFileUpload = (field, label, inputId, accept = ".pdf,.png,.jpg,.jpeg") => {
    const file = formData[field];
    const isFile = file instanceof File;
    const isExistingPath = typeof file === "string" && file.length > 0;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textAlign: isArabic ? "right" : "left" }}>
          {label}
        </Typography>
        <Paper
          elevation={0}
          onClick={() => !isSubmitting && !isFile && !isExistingPath && document.getElementById(inputId)?.click()}
          sx={{
            border: validationErrors[field]
              ? "2px solid #d32f2f"
              : isFile || isExistingPath
                ? "2px solid #4CAF50"
                : "2px dashed #C5D1D8",
            borderRadius: 2,
            minHeight: 120,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isSubmitting || isFile || isExistingPath ? "default" : "pointer",
            bgcolor: isFile || isExistingPath ? "rgba(76, 175, 80, 0.02)" : "transparent",
          }}
        >
          {isFile || isExistingPath ? (
            <Box sx={{ width: "100%", display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                <FileIcon sx={{ color: "#4CAF50", fontSize: 32, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1, textAlign: isArabic ? "right" : "left" }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: isExistingPath ? "pointer" : "default",
                      color: isExistingPath ? "primary.main" : "text.primary",
                    }}
                    onClick={() => {
                      if (isExistingPath) {
                        window.open(`${process.env.REACT_APP_API_URL}/api/student-admissions/download/${file}`, "_blank");
                      }
                    }}
                  >
                    {isFile ? file.name : t("studentDashboard.submissions.ausbildungForm.currentFile")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isFile ? `${(file.size / (1024 * 1024)).toFixed(2)} MB` : t("studentDashboard.submissions.ausbildungForm.alreadyUploaded")}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "flex-end", sm: "initial" }, gap: 1, flexShrink: 0 }}>
                <SuccessIcon sx={{ color: "#4CAF50" }} />
                <Button
                  size="small"
                  disabled={isSubmitting}
                  onClick={(event) => {
                    event.stopPropagation();
                    document.getElementById(inputId)?.click();
                  }}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  {t("studentDashboard.submissions.edit.change")}
                </Button>
                <IconButton
                  color="error"
                  size="small"
                  disabled={isSubmitting}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteFile(field);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 1 }}>
              <UploadIcon sx={{ fontSize: 44, mb: 1.5, color: "#628FFF", opacity: 0.8 }} />
              <Typography color="text.primary" variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                {t("studentDashboard.submissions.placeholders.dragFiles", { defaultValue: isArabic ? "اختر ملفاً" : "Choose a file" })}
              </Typography>
              <Typography color="text.secondary" variant="caption" sx={{ display: "block" }}>
                {field === "applicationDocuments" ? "PDF · max. 15 MB" : t("studentDashboard.submissions.ausbildungForm.maximumFileSize")}
              </Typography>
            </Box>
          )}
          <input id={inputId} type="file" hidden disabled={isSubmitting} onChange={handleFileUpload(field)} accept={accept} />
        </Paper>
        {validationErrors[field] && (
          <Typography color="error" variant="caption" role="alert" sx={{ display: "block", mt: 0.75 }}>
            {validationErrors[field]}
          </Typography>
        )}
      </Box>
    );
  };

  const renderPersonalFields = () => (
    <>
      <Grid item xs={12} sm={6}>
        <TextField required fullWidth disabled={isSubmitting} error={Boolean(validationErrors.firstName)} helperText={validationErrors.firstName} label={t("studentDashboard.submissions.fields.firstName")} value={formData.firstName} onChange={handleChange("firstName")} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField required fullWidth disabled={isSubmitting} error={Boolean(validationErrors.lastName)} helperText={validationErrors.lastName} label={t("studentDashboard.submissions.fields.lastName")} value={formData.lastName} onChange={handleChange("lastName")} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField required fullWidth disabled={isSubmitting} error={Boolean(validationErrors.email)} helperText={validationErrors.email} type="email" label={t("studentDashboard.submissions.fields.email")} value={formData.email} onChange={handleChange("email")} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField required fullWidth disabled={isSubmitting} error={Boolean(validationErrors.phone)} helperText={validationErrors.phone} type="tel" label={t("studentDashboard.submissions.fields.phone")} value={formData.phone} onChange={handleChange("phone")} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField required fullWidth disabled={isSubmitting} error={Boolean(validationErrors.nationality)} helperText={validationErrors.nationality} label={t("studentDashboard.submissions.fields.nationality")} value={formData.nationality} onChange={handleChange("nationality")} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField required fullWidth disabled={isSubmitting} error={Boolean(validationErrors.currentCountry)} helperText={validationErrors.currentCountry} label={t("studentDashboard.submissions.ausbildungForm.currentCountry")} value={formData.currentCountry} onChange={handleChange("currentCountry")} />
      </Grid>
    </>
  );

  const renderAusbildungFields = () => (
    <>
      <Grid item xs={12}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, bgcolor: "#F7F9FA", border: "1px solid #E3E7EA" }}>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 2 }}>{t("studentDashboard.submissions.ausbildungForm.requirementsTitle")}</Typography>
          <Typography><strong>{t("studentDashboard.submissions.ausbildungForm.age")}:</strong> {positionRequirements?.ageRequirement || "--"}</Typography>
          <Typography><strong>{t("studentDashboard.submissions.ausbildungForm.languageLevel")}:</strong> {positionRequirements?.requiredLanguageLevel || "--"}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl required fullWidth error={Boolean(validationErrors.desiredProgram)}>
          <InputLabel>{t("studentDashboard.submissions.fields.desiredAusbildung", { defaultValue: "Desired Ausbildung" })}</InputLabel>
          <Select
            value={formData.desiredProgram}
            label={t("studentDashboard.submissions.fields.desiredAusbildung", { defaultValue: "Desired Ausbildung" })}
            onChange={handleChange("desiredProgram")}
            disabled={isSubmitting || lockDesiredProgram}
          >
            <MenuItem value="">
              <em>{t("studentDashboard.submissions.placeholders.select")}</em>
            </MenuItem>
            {ausbildungOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          {validationErrors.desiredProgram && <Typography color="error" variant="caption" sx={{ mt: 0.5, mx: 1.75 }}>{validationErrors.desiredProgram}</Typography>}
        </FormControl>
      </Grid>
    </>
  );

  const renderLanguageFields = () => (
    <>
      <Grid item xs={12} sm={6}>
        <FormControl required fullWidth error={Boolean(validationErrors.germanLevel)} disabled={isSubmitting}>
          <InputLabel>{t("studentDashboard.submissions.fields.germanLevel")}</InputLabel>
          <Select value={formData.germanLevel} label={t("studentDashboard.submissions.fields.germanLevel")} onChange={handleChange("germanLevel")}>
            <MenuItem value="">
              <em>{t("studentDashboard.submissions.placeholders.select")}</em>
            </MenuItem>
            {languageLevels.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
          {validationErrors.germanLevel && <Typography color="error" variant="caption" sx={{ mt: 0.5, mx: 1.75 }}>{validationErrors.germanLevel}</Typography>}
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t("studentDashboard.submissions.fields.otherLanguages")}
          value={formData.otherLanguages}
          onChange={handleChange("otherLanguages")}
          disabled={isSubmitting}
        />
      </Grid>
    </>
  );

  const renderExperienceUploads = () => {
    const files = Array.isArray(formData.experienceCertificates) ? formData.experienceCertificates : [];
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textAlign: isArabic ? "right" : "left" }}>
          {t("studentDashboard.submissions.ausbildungForm.experienceCertificates")}
        </Typography>
        <Button component="label" variant="outlined" disabled={isSubmitting} sx={{ width: "100%", minHeight: 72 }}>
          <UploadIcon sx={{ mx: 1 }} />
          {t("studentDashboard.submissions.ausbildungForm.chooseExperienceFiles")}
          <input hidden multiple type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleMultipleFileUpload("experienceCertificates")} />
        </Button>
        {files.map((file, index) => (
          <Paper key={`${file.name || file}-${index}`} elevation={0} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, p: 1.25, border: "1px solid #DDE3E7" }}>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{file instanceof File ? file.name : t("studentDashboard.submissions.ausbildungForm.documentNumber", { number: index + 1 })}</Typography>
            <IconButton color="error" size="small" onClick={() => handleDeleteFile("experienceCertificates", index)}><DeleteIcon fontSize="small" /></IconButton>
          </Paper>
        ))}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7, textAlign: isArabic ? "right" : "left" }}>
          {t("studentDashboard.submissions.ausbildungForm.experienceNote")}
        </Typography>
      </Box>
    );
  };

  const renderDocumentFields = () => (
    <>
      <Grid item xs={12}>
        <Paper sx={{ p: 3, mb: 2, borderRadius: 3, bgcolor: "#FFF9E6", border: "1px solid #F0D36A" }}>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>{t("studentDashboard.submissions.ausbildungForm.documentsTitle")}</Typography>
          <Typography>{t("studentDashboard.submissions.ausbildungForm.documentsHint")}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        {renderFileUpload("cv", t("studentDashboard.submissions.ausbildungForm.cv"), "ausbildung-cv")}
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2, lineHeight: 1.7 }}>{t("studentDashboard.submissions.ausbildungForm.cvNote")}</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        {renderFileUpload("motivationLetter", t("studentDashboard.submissions.fields.motivationLetter"), "ausbildung-motivation")}
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2, lineHeight: 1.7 }}>{t("studentDashboard.submissions.ausbildungForm.motivationNote")}</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        {renderFileUpload("highSchoolDocument", t("studentDashboard.submissions.fields.schoolCertificate", { defaultValue: "School Certificate" }), "ausbildung-school")}
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2, lineHeight: 1.7 }}>{t("studentDashboard.submissions.ausbildungForm.schoolCertificateNote")}</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        {renderFileUpload("certifications", t("studentDashboard.submissions.ausbildungForm.germanB2Certificate"), "ausbildung-language-certificate")}
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2, lineHeight: 1.7 }}>{t("studentDashboard.submissions.ausbildungForm.germanB2Note")}</Typography>
      </Grid>
      <Grid item xs={12}>
        {renderExperienceUploads()}
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: "#F7F9FA", border: "1px solid #E3E7EA" }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            {t("studentDashboard.submissions.fields.audioMessage", { defaultValue: "Audio Message" })}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ direction: isArabic ? "rtl" : "ltr", textAlign: isArabic ? "right" : "left", lineHeight: 1.8 }}
          >
            {t("studentDashboard.submissions.audioRecordingNote")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, flexWrap: "wrap" }}>
            <Button disabled={isSubmitting} variant={isRecording ? "outlined" : "contained"} color={isRecording ? "error" : "primary"} onClick={isRecording ? stopRecording : startRecording}>
              {isRecording ? t("studentDashboard.submissions.ausbildungForm.stopRecording") : t("studentDashboard.submissions.ausbildungForm.startRecording")}
            </Button>
            <Typography fontWeight={800}>{`${Math.floor(recordingSeconds / 60)}:${String(recordingSeconds % 60).padStart(2, "0")} / 4:00`}</Typography>
          </Box>
          {recordingError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{recordingError}</Typography>}
        </Paper>
        {renderFileUpload(
          "audioMessage",
          t("studentDashboard.submissions.ausbildungForm.audioFallback"),
          "ausbildung-audio",
          ".mp3,.wav,.m4a,.webm,.ogg,audio/*"
        )}
      </Grid>
    </>
  );

  const reviewEntries = [
    ["firstName", t("studentDashboard.submissions.fields.firstName")],
    ["lastName", t("studentDashboard.submissions.fields.lastName")],
    ["email", t("studentDashboard.submissions.fields.email")],
    ["phone", t("studentDashboard.submissions.fields.phone")],
    ["nationality", t("studentDashboard.submissions.fields.nationality")],
    ["currentCountry", t("studentDashboard.submissions.ausbildungForm.currentCountry")],
    ["desiredProgram", t("studentDashboard.submissions.fields.desiredAusbildung", { defaultValue: "Desired Ausbildung" })],
    ["germanLevel", t("studentDashboard.submissions.fields.germanLevel")],
    ["otherLanguages", t("studentDashboard.submissions.fields.otherLanguages")],
    ["cv", t("studentDashboard.submissions.ausbildungForm.cv")],
    ["certifications", t("studentDashboard.submissions.ausbildungForm.germanB2Certificate")],
    ["motivationLetter", t("studentDashboard.submissions.fields.motivationLetter")],
    ["highSchoolDocument", t("studentDashboard.submissions.fields.schoolCertificate", { defaultValue: "School Certificate" })],
    ["audioMessage", t("studentDashboard.submissions.fields.audioMessage", { defaultValue: "Audio Message" })],
    ["experienceCertificates", t("studentDashboard.submissions.ausbildungForm.experienceCertificates")],
  ];

  if (isEdit || activeStep === -1) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {renderPersonalFields()}
          {renderAusbildungFields()}
          {renderLanguageFields()}
          {renderDocumentFields()}
        </Grid>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {activeStep === 0 && <>{renderPersonalFields()}{renderAusbildungFields()}{renderLanguageFields()}</>}
        {activeStep === 1 && renderDocumentFields()}
        {activeStep === 2 && <>
          {reviewEntries.map(([key, label]) => {
            const value = formData[key];
            return (
              <Grid item xs={12} sm={6} key={key}>
                <Paper sx={{ p: 2, borderRadius: 3, bgcolor: "#F7F9FA", overflowWrap: "anywhere" }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {label}
                  </Typography>
                  {Array.isArray(value)
                    ? (value.length ? value.map((item) => item instanceof File ? item.name : item).join(", ") : t("studentDashboard.submissions.notProvided"))
                    : value instanceof File ? value.name : value || t("studentDashboard.submissions.notProvided")}
                </Paper>
              </Grid>
            );
          })}
          <Grid item xs={12}>
            <FormControlLabel
              required
              control={<Checkbox disabled={isSubmitting} checked={Boolean(formData.consentGiven)} onChange={(event) => handleChange("consentGiven")({ target: { value: event.target.checked } })} />}
              label={
                <Typography variant="body2">
                  {t("studentDashboard.submissions.ausbildungForm.consent")}{" "}
                  <Box component="a" href="/privacy-policy" target="_blank" rel="noreferrer">
                    {t("studentDashboard.submissions.ausbildungForm.privacyLink")}
                  </Box>
                </Typography>
              }
            />
            {validationErrors.consentGiven && <Typography color="error" variant="caption" role="alert" sx={{ display: "block", mt: 0.5 }}>{validationErrors.consentGiven}</Typography>}
          </Grid>
        </>}
      </Grid>
    </Box>
  );
};

export default AusbildungSubmissionForm;
